import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Globe, RefreshCw, ExternalLink, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Fase 6.8 pt.7 · Backfill AI dei siti web dei produttori.
 *
 * L'admin lancia il backfill sui top N produttori (per numero vini).
 * L'AI risponde solo con URL ad alta confidenza; il backend verifica
 * HEAD/GET 2xx entro 5s prima di salvare. Se la verifica fallisce,
 * l'URL viene scartato e il produttore resta senza sito.
 * Idempotente: chi ha già un sito viene skippato.
 */
export default function AdminProducerWebsites() {
    const [limit, setLimit] = useState(100);
    const [maxAi, setMaxAi] = useState(30);
    const [dryRun, setDryRun] = useState(false);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);

    const run = async () => {
        setRunning(true);
        setResult(null);
        try {
            const { data } = await api.post("/admin/producers/backfill-websites", null, {
                params: { limit, max_ai_per_run: maxAi, dry_run: dryRun },
                timeout: 240_000,
            });
            setResult(data);
            toast.success(dryRun ? "Dry-run completato" : `Backfill completato · ${data.updated_wines} vini aggiornati`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore backfill");
        } finally {
            setRunning(false);
        }
    };

    return (
        <div className="p-6 max-w-4xl mx-auto" data-testid="admin-producer-websites-page">
            <h1 className="text-2xl flex items-center gap-2 mb-1">
                <Globe className="w-5 h-5" strokeWidth={1.5} />
                Backfill siti produttore (AI)
            </h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6 max-w-2xl">
                Popola <code>producer_website</code> per i produttori con più vini.
                L'AI propone l'URL solo se molto certa; il backend verifica
                HEAD/GET 2xx prima di salvare. Chi ha già un sito viene skippato.
                <b> Idempotente e sicuro.</b>
            </p>

            <div className="bg-white border border-[hsl(var(--border))] rounded-sm p-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                        <Label className="co-caption mb-2 block">Produttori (limit)</Label>
                        <Input type="number" min="1" max="500" value={limit}
                            onChange={(e) => setLimit(Number(e.target.value))}
                            data-testid="prod-web-limit" className="bg-white" />
                    </div>
                    <div>
                        <Label className="co-caption mb-2 block">Chiamate AI max per run</Label>
                        <Input type="number" min="1" max="200" value={maxAi}
                            onChange={(e) => setMaxAi(Number(e.target.value))}
                            data-testid="prod-web-max-ai" className="bg-white" />
                    </div>
                    <div className="flex items-end pb-1">
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={dryRun}
                                onChange={(e) => setDryRun(e.target.checked)}
                                data-testid="prod-web-dry-run" />
                            <span>Dry-run (non scrive)</span>
                        </label>
                    </div>
                </div>

                <div className="flex items-start gap-2 text-xs text-[hsl(var(--muted-foreground))] p-3 border border-amber-200 bg-amber-50 rounded-sm">
                    <AlertTriangle className="w-4 h-4 shrink-0 text-amber-700" strokeWidth={1.5} />
                    <div>
                        L'AI risponde con <b>url=null</b> se non è certa (policy
                        anti-allucinazione forte). I 429 quota Gemini vengono
                        gestiti col fallback automatico su chiave platform.
                    </div>
                </div>

                <button type="button" onClick={run} disabled={running}
                    className="co-btn-primary" data-testid="prod-web-run">
                    <RefreshCw className={`w-4 h-4 ${running ? "animate-spin" : ""}`} />
                    {running ? "In esecuzione…" : (dryRun ? "Anteprima (dry-run)" : "Lancia backfill")}
                </button>
            </div>

            {result && (
                <div className="mt-6 bg-white border border-[hsl(var(--primary))]/40 rounded-sm p-5" data-testid="prod-web-result">
                    <div className="co-caption mb-3">Esito {result.dry_run ? "(DRY-RUN)" : ""}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-4">
                        <Stat label="Produttori scanned" value={result.scanned_producers} />
                        <Stat label="Candidati (senza sito)" value={result.candidates} />
                        <Stat label="Chiamate AI" value={result.ai_calls} />
                        <Stat label="URL verificati OK" value={result.verified_ok} highlight />
                        <Stat label="Non certi (AI)" value={result.ai_uncertain} />
                        <Stat label="Verify fallito" value={result.verified_fail} />
                        <Stat label="Vini aggiornati" value={result.updated_wines} highlight />
                    </div>
                    {result.examples?.length > 0 && (
                        <div>
                            <div className="co-caption mb-2 text-xs">Esempi</div>
                            <ul className="space-y-1 text-sm">
                                {result.examples.map((ex, i) => (
                                    <li key={i} className="flex items-center gap-2" data-testid={`prod-web-example-${i}`}>
                                        <span className="min-w-[200px] truncate">{ex.producer}</span>
                                        <span className="text-[10px] text-[hsl(var(--muted-foreground))]">({ex.confidence} · {ex.wines} vini)</span>
                                        <a href={ex.url} target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--primary))] underline flex items-center gap-1">
                                            {ex.url} <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, highlight = false }) {
    return (
        <div className={`p-3 rounded-sm border ${highlight ? "border-[hsl(var(--primary))] bg-[#FDFBF7]" : "border-[hsl(var(--border))]"}`}>
            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{label}</div>
            <div className={`text-xl ${highlight ? "text-[hsl(var(--primary))]" : ""}`}>{value ?? 0}</div>
        </div>
    );
}
